import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { ContadorDeMensualidad } from '../../deuda-descompuesta/componentes/ContadorDeMensualidad.jsx';
import { consultarMensualidades, obtenerRecordatorioDeTarjeta } from '../api/msiApi.js';
import { RecordatorioDePago } from './RecordatorioDePago.jsx';
import { importe as formatearImporte, LEYENDA_REPARTO_DE_DEUDA } from '../formato.js';

/**
 * BLOQUE DE MESES SIN INTERESES DEL DETALLE DE LA TARJETA (RF-077.2).
 *
 * Muestra lo que cae en el corte próximo y los planes vigentes de ESTA tarjeta. La
 * cifra es un REPARTO DE DEUDA YA CONTADA, NO DEUDA ADICIONAL: nunca se suma junto
 * a la deuda de arriba y se etiqueta como parte de ella (D-53, RN-103c).
 *
 * AQUÍ SÍ VA EL TEXTO COMPLETO DEL RECORDATORIO DE RN-124, porque LA CIFRA ES DE LA
 * TARJETA y es donde tiene sentido leerla junto a la deuda (RN-124b.2). Es EL MISMO
 * objeto que muestra el panel del plan: si las dos pantallas enseñaran números
 * distintos, sería defecto (RN-124c).
 */
export function BloqueDeMsiDeLaTarjeta({ cuenta }) {
  const hoy = obtenerFechaDeNegocioHoy();

  const [datos, setDatos] = useState(null);
  const [recordatorio, setRecordatorio] = useState(null);

  /*
   * RN-752 (incremento 20/21) · LISTA EXPLÍCITA DE TIPO, nunca `esCredito`. Los
   * meses sin intereses son EXCLUSIVOS de la tarjeta de crédito: un préstamo también
   * lleva esa bandera —dice «es pasivo»— y jamás ofrece MSI (CA-20-19). Su plan de
   * pagos es otro y vive en su propia pantalla.
   */
  const esTarjetaDeCredito = cuenta?.claveTipoCuenta === 'CREDITO';

  useEffect(() => {
    if (!esTarjetaDeCredito) return;

    (async () => {
      try {
        setDatos(await consultarMensualidades({ idCuenta: cuenta.idCuenta, fechaLocal: hoy }));
        setRecordatorio(await obtenerRecordatorioDeTarjeta(cuenta.idCuenta, hoy));
      } catch {
        // El bloque es informativo: si falla, el detalle de la tarjeta sigue
        // viéndose entero y ninguna cifra se inventa.
      }
    })();
  }, [cuenta, hoy, esTarjetaDeCredito]);

  if (!esTarjetaDeCredito) return null;

  const renglones = datos?.mensualidadesDelMes ?? [];
  const total = datos?.totalesPorTarjeta?.[0] ?? null;

  return (
    <div className="card" style={{ margin: 12 }} data-prueba="bloque-msi-de-tarjeta">
      <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>MESES SIN INTERESES</h4>

      <RecordatorioDePago
        recordatorio={recordatorio}
        idCuenta={cuenta.idCuenta}
        esPropio={cuenta.esPropia}
      />

      {total ? (
        <>
          <div className="fila">
            <span className="crece">Cae en este corte</span>
            <b>{formatearImporte(total.totalImputadoAlMes, cuenta.codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Falta por caer en cortes futuros</span>
            <b>{formatearImporte(total.totalPendiente, cuenta.codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Planes con mensualidades</span>
            <b>{total.cantidadPlanesVigentes}</b>
          </div>
          {/* REGLA, NO ESTILO (D-53, RN-103c): NUNCA se suma a la deuda de arriba. */}
          <span className="ayuda">{LEYENDA_REPARTO_DE_DEUDA}</span>
        </>
      ) : (
        <span className="ayuda">Esta tarjeta no tiene compras a meses con mensualidades por caer.</span>
      )}

      {renglones.length > 0 && (
        <div className="lista">
          {renglones.map((renglon) => (
            <Link className="fila" key={renglon.idMensualidad} to={`/cuentas/meses/planes/${renglon.idPlan}`}>
              <div className="crece">
                <b>{renglon.descripcionDelGasto || 'Compra a meses'}</b>
                {/* EL CONTADOR VA CON LA PIEZA COMPARTIDA, NO CON UN RECUADRO PROPIO
                    (RNF-132, RN-707, D-393). Antes este bloque dibujaba su propio
                    `.chip`, y por eso la corrección de contraste del contador no lo
                    alcanzó: la regla se arregló en la pieza y aquí no había pieza.
                    El texto «n/m» sigue llegando YA ARMADO del servidor (RNF-038):
                    este componente no concatena, no divide y no interpreta nada, así
                    que el cambio es de PRESENTACIÓN y no mueve ningún comportamiento.

                    `esHistorica` NO SE PASA A PROPÓSITO: el contrato de mensualidades
                    todavía no trae ese dato y la semántica de «histórica» es del
                    incremento 19, su dueño (RN-707). Pasar un campo inexistente
                    aparentaría una distinción que el servidor no está informando. El 19
                    solo tiene que añadir el dato y pasarlo aquí: la pieza ya lo sabe
                    pintar y la leyenda ya existe. */}
                <ContadorDeMensualidad contador={renglon.contadorDeMensualidad} />
                <small>corte {formatearFecha(renglon.fechaCorte)}</small>
              </div>
              <span className="imp">{formatearImporte(renglon.importeImputadoAlMes, renglon.codigoISO)}</span>
            </Link>
          ))}
        </div>
      )}

      <Link className="btn sec ancho" to="/cuentas/meses">Ver todos los meses sin intereses</Link>
    </div>
  );
}
